import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    contouringToolsParkedState,
    selectedToolState,
    Tools,
} from '~/state/atoms/toolsAtoms';

/**
 * Tools whose drag WRITES to the selected ROI. These are the ones a box
 * on screen has to park: the hazard is a press that misses a resize
 * handle by a few pixels and lands on the image as a stroke instead.
 *
 * `transform` is in the list even though it paints nothing — it drags the
 * whole structure, which is the same class of unintended, hard-to-notice
 * edit. Pan, zoom, rotate, measurement and POI moves are NOT: they cannot
 * change a contour, and parking them would take working navigation away
 * from a user who is trying to place the box.
 */
const ROI_EDITING_TOOLS: readonly Tools[] = [
    'brush',
    'line',
    'deform',
    'lineDeform',
    'smoothBrush',
    'transform',
    'rectangle-select',
    'cross-section',
];

/**
 * Where a parked tool is sent. Pan is the neutral tool, so parking leaves
 * the viewport navigable.
 */
const PARKED_TOOL: Tools = 'pan';

/**
 * Whether `tool` is one this hook parks. EXPORTED so the toolbar buttons
 * can be disabled from the very same list that does the parking: hand-listing
 * them there would let the two drift, and a tool that is parked but still
 * reads as armed is exactly the "UI lies about its state" defect.
 */
export const isParkedContouringTool = (tool: Tools): boolean =>
    ROI_EDITING_TOOLS.includes(tool);

/**
 * Whether a box is currently parking the ROI-editing tools. For the button
 * that must show itself unavailable while it lasts; the parking itself is
 * done by {@link useParkedContouringTool} alone.
 */
export function useContouringToolsParked(): boolean {
    return useRecoilValue(contouringToolsParkedState);
}

/**
 * Parks the ROI-editing tools while `active` is true, and restores the
 * tool that was in use before parking when it goes false (or when the
 * caller unmounts).
 *
 * While a draggable VOI box is on screen, a drag that starts slightly off
 * a handle must not paint. Scope is the WHOLE time the box is shown, not
 * just the handle drag — the stray drag this exists to stop is by
 * definition one that never reaches a handle.
 *
 * It parks through `selectedToolState`, never through `ToolManager`: the
 * manager keeps no record of a tool it disposes, so parking there could
 * not restore the tool afterwards.
 *
 * Parking is CONTINUOUS while active: selecting a contouring tool from
 * the toolbar with the box up parks it again, because "the box is on
 * screen" is the parking condition itself. What is restored is the
 * FIRST tool parked — the one the user was working with when the box
 * appeared, or, if that was already a harmless tool, the contouring tool
 * they reached for while the box was up. Either way the request is
 * honoured once the box is gone rather than dropped.
 */
export function useParkedContouringTool(active: boolean): void {
    const [selectedTool, setSelectedTool] = useRecoilState(selectedToolState);
    const setParked = useSetRecoilState(contouringToolsParkedState);
    // The tool to restore, or null when nothing is currently parked. A
    // ref, not state: it must be readable from the unmount cleanup
    // without becoming a dependency that re-runs the effect.
    const parkedFromRef = useRef<Tools | null>(null);

    // Reassigned every render so the one-shot unmount effect below —
    // whose closure is captured once, at mount — always sees the LATEST
    // selected tool. Same idiom as useVoiRegionRestriction's overlay
    // release and the rectangle eraser's disarm-on-unmount.
    const restoreRef = useRef(() => {});
    restoreRef.current = () => {
        const previous = parkedFromRef.current;
        if (previous === null) {
            return;
        }
        parkedFromRef.current = null;
        // Only restore over the parked tool itself. If the user has since
        // chosen some other non-parked tool (pan -> zoom), that choice is
        // newer than ours and must not be clobbered.
        if (selectedTool === PARKED_TOOL) {
            setSelectedTool(previous);
        }
    };

    useEffect(() => {
        if (!active) {
            restoreRef.current();
            return;
        }
        if (isParkedContouringTool(selectedTool)) {
            // First park wins: a later selection must not overwrite what
            // the user was doing before the box appeared.
            if (parkedFromRef.current === null) {
                parkedFromRef.current = selectedTool;
            }
            setSelectedTool(PARKED_TOOL);
        }
    }, [active, selectedTool, setSelectedTool]);

    // Publishes the parking so the toolbar can render the parked tools as
    // unavailable. Kept as its own effect, and cleared on unmount, so the
    // flag can never outlive the box that raised it — a toolbar left
    // permanently disabled would be the worse half of the same lie.
    useEffect(() => {
        setParked(active);
        return () => setParked(false);
    }, [active, setParked]);

    useEffect(() => () => restoreRef.current(), []);
}
